/**
 * Service for fetching various feed endpoints.
 *
 * Responsibilities:
 * - Fetch "For You" feed and "Trending" feed with session header.
 * - Fetch liked feed and SFW feed.
 * - Fetch feed modules configuration.
 */
import type {
  IGifList,
  IFeedModulesResponse,
  TrendingFeedVariation,
  ForYouSourceEnum,
} from "../types";

import type { ApiClient } from "@/common/api/http/types";

export default class FeedsService {
  public constructor(private readonly client: ApiClient) {}

  async getForYouFeed(props: {
    page: number;
    count: number;
    source?: ForYouSourceEnum;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    if (props.source) {
      query.append("source", props.source);
    }

    return await this.client.get(`v2/feeds/for-you?${query.toString()}`, {
      "X-Session-Id": await this.client.getSessionId(),
    });
  }

  async getTrendingFeed(props: {
    page: number;
    count: number;
    variation: TrendingFeedVariation;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    return await this.client.get(`v2/feeds/trending/${props.variation}?${query.toString()}`, {
      "X-Session-Id": await this.client.getSessionId(),
    });
  }

  async getLikedFeed(props: {
    page: number;
    count: number;
    type?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    if (props.type) {
      query.append("type", props.type);
    }

    return await this.client.get(`v2/feeds/liked?${query.toString()}`);
  }

  async getSFWFeed(params: { page: number; count: number }): Promise<IGifList> {
    const query = new URLSearchParams();

    query.append("page", params.page.toString());
    query.append("count", params.count.toString());

    return await this.client.get(`v2/feeds/sfw?${query.toString()}`);
  }

  async getFeedModules(): Promise<IFeedModulesResponse> {
    return await this.client.get("/v2/feeds/modules");
  }
}
